/**
 * Arbiter Sports API client.
 *
 * Pure async functions, 1:1 with the backend endpoints under
 * /v1/schools/:schoolId/integrations/arbiter/... The school in the path is
 * also sent as `x-school-id`, and the hooks key their queries on it.
 *
 * Errors: Arbiter-side failures come back from the backend as a typed
 * 502/500 body (`ArbiterErrorEnvelope`, not the usual `{ error }` envelope).
 * The domain catch re-raises that body as an `ArbiterApiError` carrying the
 * typed `kind`; any other refusal stays the client's `ApiError`.
 *
 * Component code (U5) inspects `ArbiterApiError#kind` to render the
 * specific banner.
 */

import { z } from 'zod'
import {
  ArbiterErrorEnvelopeSchema,
  CreateArbiterTeamSectionLinkResponseSchema,
  DeleteArbiterTeamSectionLinkResponseSchema,
  GetArbiterConfiguredResponseSchema,
  GetArbiterGamesResponseSchema,
  GetArbiterTeamsResponseSchema,
  ListArbiterTeamSectionLinksResponseSchema,
  UpdateArbiterTeamSectionLinkResponseSchema,
  type ArbiterErrorEnvelope,
  type CreateArbiterTeamSectionLinkRequest,
  type CreateArbiterTeamSectionLinkResponse,
  type DeleteArbiterTeamSectionLinkResponse,
  type GetArbiterConfiguredResponse,
  type GetArbiterGamesRequest,
  type GetArbiterGamesResponse,
  type GetArbiterTeamsResponse,
  type ListArbiterTeamSectionLinksResponse,
  type UpdateArbiterTeamSectionLinkRequest,
  type UpdateArbiterTeamSectionLinkResponse
} from '@school-journey/shared-types'

import { refusalsAs, type ApiError } from '@/lib/backend-client/api-error'
import type { BackendClient } from '@/lib/backend-client/client'
import { path, type Endpoint } from '@/lib/backend-client/endpoint'

export class ArbiterApiError extends Error {
  readonly kind: ArbiterErrorEnvelope['kind']
  readonly statusCode?: number
  constructor(envelope: ArbiterErrorEnvelope) {
    super(envelope.message ?? envelope.kind)
    this.name = 'ArbiterApiError'
    this.kind = envelope.kind
    this.statusCode = envelope.statusCode
  }
}

/**
 * An Arbiter envelope (kept whole as `ApiError.details`, since it has no
 * `error` key) becomes an `ArbiterApiError`; anything else stays the
 * `ApiError` it was.
 */
function arbiterErrorOf(error: ApiError): Error {
  const envelope = ArbiterErrorEnvelopeSchema.safeParse(error.details)
  return envelope.success ? new ArbiterApiError(envelope.data) : error
}

function arbiterRequest<T>(
  client: BackendClient,
  schoolPublicId: string,
  endpoint: Endpoint,
  schema: z.ZodType<T>
): Promise<T> {
  return refusalsAs(client.request(endpoint, schema, { schoolId: schoolPublicId }), arbiterErrorOf)
}

const base = (schoolPublicId: string) => path`/v1/schools/${schoolPublicId}/integrations/arbiter`

// ----- Configured -----

export function getArbiterConfigured(
  client: BackendClient,
  schoolPublicId: string
): Promise<GetArbiterConfiguredResponse> {
  return arbiterRequest(
    client,
    schoolPublicId,
    { method: 'GET', path: `${base(schoolPublicId)}/configured` },
    GetArbiterConfiguredResponseSchema
  )
}

// ----- Games -----

export function getArbiterGames(
  client: BackendClient,
  schoolPublicId: string,
  params: GetArbiterGamesRequest
): Promise<GetArbiterGamesResponse> {
  return arbiterRequest(
    client,
    schoolPublicId,
    {
      method: 'GET',
      path: `${base(schoolPublicId)}/games`,
      query: { startDate: params.startDate || undefined, endDate: params.endDate || undefined }
    },
    GetArbiterGamesResponseSchema
  )
}

// ----- Teams -----

export function getArbiterTeams(client: BackendClient, schoolPublicId: string): Promise<GetArbiterTeamsResponse> {
  return arbiterRequest(
    client,
    schoolPublicId,
    { method: 'GET', path: `${base(schoolPublicId)}/teams` },
    GetArbiterTeamsResponseSchema
  )
}

// ----- Team ↔ Section links -----

export function listArbiterTeamSectionLinks(
  client: BackendClient,
  schoolPublicId: string
): Promise<ListArbiterTeamSectionLinksResponse> {
  return arbiterRequest(
    client,
    schoolPublicId,
    { method: 'GET', path: `${base(schoolPublicId)}/team-section-links` },
    ListArbiterTeamSectionLinksResponseSchema
  )
}

export function createArbiterTeamSectionLink(
  client: BackendClient,
  schoolPublicId: string,
  body: CreateArbiterTeamSectionLinkRequest
): Promise<CreateArbiterTeamSectionLinkResponse> {
  return arbiterRequest(
    client,
    schoolPublicId,
    { method: 'POST', path: `${base(schoolPublicId)}/team-section-links`, body },
    CreateArbiterTeamSectionLinkResponseSchema
  )
}

export function updateArbiterTeamSectionLink(
  client: BackendClient,
  schoolPublicId: string,
  linkPublicId: string,
  body: UpdateArbiterTeamSectionLinkRequest
): Promise<UpdateArbiterTeamSectionLinkResponse> {
  return arbiterRequest(
    client,
    schoolPublicId,
    { method: 'PUT', path: `${base(schoolPublicId)}${path`/team-section-links/${linkPublicId}`}`, body },
    UpdateArbiterTeamSectionLinkResponseSchema
  )
}

export function deleteArbiterTeamSectionLink(
  client: BackendClient,
  schoolPublicId: string,
  linkPublicId: string
): Promise<DeleteArbiterTeamSectionLinkResponse> {
  return arbiterRequest(
    client,
    schoolPublicId,
    { method: 'DELETE', path: `${base(schoolPublicId)}${path`/team-section-links/${linkPublicId}`}` },
    DeleteArbiterTeamSectionLinkResponseSchema
  )
}

// ----- School sections (team-section picker) -----

/**
 * The fields the team-section picker reads from `GET /v1/schools/:id/sections`.
 * That endpoint has no shared-types response schema; this projection is
 * web-local and checks only what the picker uses (zod strips the rest).
 */
const ArbiterSchoolSectionsResponseSchema = z.object({
  data: z
    .array(z.object({ id: z.string(), name: z.string(), offeringType: z.string().nullable().optional() }))
    .optional()
})

export interface ArbiterSchoolSection {
  id: string
  name: string
  offeringType?: string | null
}

/** Every section of the school (up to 1000), narrowed to what the picker shows. */
export async function listArbiterSchoolSections(
  client: BackendClient,
  schoolPublicId: string
): Promise<ArbiterSchoolSection[]> {
  const response = await client.request(
    { method: 'GET', path: path`/v1/schools/${schoolPublicId}/sections`, query: { limit: 1000 } },
    ArbiterSchoolSectionsResponseSchema,
    { schoolId: schoolPublicId }
  )
  return (response.data ?? []).map(s => ({ id: s.id, name: s.name, offeringType: s.offeringType ?? null }))
}
